import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/card";
import { ChartContainer, ChartTooltip } from "@/components/chart";
import type { JiraMetricsControlChartPoint } from "@/http/api-types";
import { CartesianGrid, Scatter, ScatterChart, XAxis, YAxis } from "recharts";
import { EmptyState } from "./empty-state";
import { formatDays, formatTimestampDate, secondsToDays } from "./formatters";

type DoneDateScatterPoint = {
  ticket_key: string;
  moved_to_done_ts: number;
  cycle_days: number;
};

function DoneDateScatterTooltipContent({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: DoneDateScatterPoint }>;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload;

  return (
    <div className="grid min-w-[14rem] gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground">Ticket</span>
        <span className="font-mono font-medium text-foreground">{point?.ticket_key || "-"}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground">Done Date</span>
        <span className="font-mono font-medium text-foreground">{formatTimestampDate(point?.moved_to_done_ts)}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground">Cycle</span>
        <span className="font-mono font-medium text-foreground">{formatDays(point?.cycle_days)}</span>
      </div>
    </div>
  );
}

const extractTicketKeyFromScatterPoint = (point: unknown) => {
  if (!point || typeof point !== "object") {
    return "";
  }

  const directKey = (point as { ticket_key?: unknown }).ticket_key;
  if (typeof directKey === "string") {
    return directKey;
  }

  const payloadKey = (point as { payload?: { ticket_key?: unknown } }).payload?.ticket_key;
  if (typeof payloadKey === "string") {
    return payloadKey;
  }

  return "";
};

export function DoneDateVsCycleScatterChart({
  data,
  onTicketSelect,
}: {
  data: JiraMetricsControlChartPoint[];
  onTicketSelect: (ticketKey: string) => void;
}) {
  const chartData = data
    .map(point => ({
      ticket_key: point.ticket_key,
      moved_to_done_ts: new Date(point.moved_to_done).getTime(),
      cycle_days: secondsToDays(point.cycle_seconds),
    }))
    .filter(point => Number.isFinite(point.moved_to_done_ts));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Done Date vs Cycle Time (Ticket Scatter)</CardTitle>
        <CardDescription>
          Each dot is one completed ticket. Click a dot to filter the ticket detail table to that ticket.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <EmptyState message="No completed tickets in selected range. Try a wider date range." />
        ) : (
          <ChartContainer
            className="h-72 w-full"
            config={{ cycle_days: { label: "Cycle (workdays)", color: "#22c55e" } }}
          >
            <ScatterChart>
              <CartesianGrid />
              <XAxis
                type="number"
                dataKey="moved_to_done_ts"
                name="Done Date"
                domain={["dataMin", "dataMax"]}
                tickFormatter={value => formatTimestampDate(Number(value))}
              />
              <YAxis
                type="number"
                dataKey="cycle_days"
                name="Cycle Workdays"
                domain={[0, "auto"]}
                tickFormatter={value => `${Number(value).toFixed(1)}d`}
              />
              <ChartTooltip content={<DoneDateScatterTooltipContent />} />
              <Scatter
                data={chartData}
                fill="var(--color-cycle_days)"
                onClick={point => {
                  const ticketKey = extractTicketKeyFromScatterPoint(point);
                  if (ticketKey) {
                    onTicketSelect(ticketKey);
                  }
                }}
              />
            </ScatterChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
